<template>
  <!-- Search Section -->
  <li class="nav-item search-container">
    <div class="search-box">
      <div class="search-input-group">
        <div v-show="!statusEnrollment">
          <label for="rudeCode">Código RUDE</label>
          <InputText
            id="rudeCode"
            v-model="rueValue"
            placeholder="Ingrese código RUDE"
            :class="{ 'p-invalid': searchError }"
          />
          <Button
            icon="pi pi-search"
            label="Buscar"
            @click="findRue"
            :loading="searchLoading"
          />
        </div>

        <Button
          icon="pi pi-file"
          label="Reporte Clasificados"
          severity="success"
          v-show="statusEnrollment"
          @click="downloadReport"
          :loading="searchLoading"
        />
      </div>
      <small class="p-error" v-if="searchError">{{ searchError }}</small>
    </div>
  </li>

  <!-- Student Details Dialog -->
  <Dialog
    v-model:visible="visible"
    modal
    header="Registro de Estudiante"
    :style="{ width: '70vw' }"
    :breakpoints="{ '1199px': '80vw', '575px': '95vw' }"
  >
    <!-- Student Information -->
    <StudentInfoPanel :student="student" :loading="studentLoading" />

    <!-- Registration Form -->
    <Panel header="Documentación Requerida" class="registration-panel">
      <form @submit.prevent="handleSubmit">
        <div class="form-grid two-panel-grid">
          <!-- Left: Project Info Panel -->
          <div class="info-panel">
            <h4 class="info-header">
              <i class="pi pi-info-circle"></i> Información del Proyecto
            </h4>
            <div class="field">
              <label for="projectUrl">URL del proyecto *</label>
              <InputText
                id="projectUrl"
                v-model="formData.projectUrl"
                type="url"
                placeholder="https://drive.google.com/..."
                :class="[
                  'full-width',
                  {
                    'p-invalid':
                      v$.formData.projectUrl.$error &&
                      v$.formData.projectUrl.$dirty,
                  },
                ]"
                :disabled="formLoading"
                @blur="v$.formData.projectUrl.$touch()"
              />
              <small
                class="p-error"
                v-if="
                  v$.formData.projectUrl.$error && v$.formData.projectUrl.$dirty
                "
              >
                <!-- Show Google Drive error first if present -->
                <template
                  v-if="
                    v$.formData.projectUrl.$errors.find(
                      (e) => e.$validator === 'googleDriveUrl'
                    )
                  "
                >
                  {{
                    v$.formData.projectUrl.$errors.find(
                      (e) => e.$validator === "googleDriveUrl"
                    ).$message
                  }}
                </template>
                <template v-else>
                  {{ v$.formData.projectUrl.$errors[0].$message }}
                </template>
              </small>
            </div>
            <div class="field">
              <label for="category">Área de participación *</label>
              <Dropdown
                id="category"
                v-model="formData.category"
                :options="categories"
                optionLabel="name"
                optionValue="value"
                placeholder="Seleccione Área de Participación"
                :class="[
                  'full-width',
                  { 'p-invalid': v$.formData.category.$error },
                ]"
                :disabled="formLoading"
              />
              <small class="p-error" v-if="v$.formData.category.$error">
                {{ v$.formData.category.$errors[0].$message }}
              </small>
            </div>
          </div>

          <!-- Right: File Uploads Panel -->
          <div class="upload-section">
            <h4 class="upload-header">
              <i class="pi pi-upload"></i> Documentos requeridos
            </h4>
            <small
              class="p-error"
              v-if="missingFilesError"
              style="margin-bottom: 1rem; display: block"
            >
              {{ missingFilesError }}
            </small>
            <div
              v-for="(item, index) in uploads"
              :key="index"
              class="upload-item improved-upload-item"
            >
              <div class="upload-label-row">
                <span class="upload-label">
                  <i class="pi pi-paperclip"></i> {{ item.label }}
                </span>
                <span v-if="item.file" class="selected-file">
                  <i class="pi pi-file"></i> {{ item.file.name }}
                </span>
              </div>
              <FileUpload
                :name="item.name"
                mode="basic"
                :chooseLabel="item.file ? 'Cambiar archivo' : item.label"
                :class="[
                  'full-width',
                  {
                    'p-invalid':
                      v$.uploads[index] &&
                      v$.uploads[index].file &&
                      v$.uploads[index].file.$error,
                  },
                ]"
                @select="onFileSelect($event, index)"
                :disabled="formLoading"
                chooseIcon="pi pi-folder-open"
                accept="application/pdf"
              />
              <small
                class="p-error"
                v-if="
                  v$.uploads[index] &&
                  v$.uploads[index].file &&
                  v$.uploads[index].file.$error
                "
              >
                {{ v$.uploads[index].file.$errors[0].$message }}
              </small>
              <small class="p-error" v-if="submitAttempted && !item.file">
                Debe subir este archivo.
              </small>
            </div>
          </div>
        </div>

        <!-- Form Actions -->
        <div class="form-actions">
          <Button
            type="button"
            label="Cancelar"
            severity="secondary"
            @click="visible = false"
            :disabled="formLoading"
          />
          <Button
            type="submit"
            label="Guardar Registro"
            icon="pi pi-check"
            :loading="formLoading"
            :disabled="v$.$invalid || formLoading"
          />
        </div>
      </form>
    </Panel>
  </Dialog>
</template>

<script setup>
import { ref, reactive, watch } from "vue";
import { useVuelidate } from "@vuelidate/core";
import { required, url } from "@vuelidate/validators";

const emit = defineEmits(["selectSchool", "getReport"]);

const props = defineProps({
  statusEnrollment: Boolean,
});

// Custom validator for Google Drive URLs
const googleDriveUrl = (value) => {
  if (!value) return false;
  // Accepts drive.google.com/file/d/ or drive.google.com/open?id= or drive.google.com/uc?id=
  return /^(https?:\/\/)?(www\.)?drive\.google\.com\/(file\/d\/|open\?id=|uc\?id=)/.test(
    value
  );
};
import axios from "@/includes/connection";
import Swal from "sweetalert2";
import StudentInfoPanel from "./StudentInfoPanel.vue";

// Search functionality
const rueValue = ref("");
const searchError = ref("");
const searchLoading = ref(false);
const studentLoading = ref(false);
const visible = ref(false);
const student = ref({});

const findRue = async () => {
  if (!rueValue.value) {
    searchError.value = "Por favor ingrese un código RUDE";
    return;
  }

  searchLoading.value = true;
  searchError.value = "";

  try {
    const response = await axios.get(
      `/institution/list/rue-by-user/${localStorage.getItem("username")}/${
        rueValue.value
      }`
    );

    if (response.data) {
      student.value = response.data;
      visible.value = true;
    } else {
      searchError.value = "No se encontró el estudiante";
    }
  } catch (error) {
    console.error(error);
    searchError.value =
      error.response?.data?.message || "Error al buscar estudiante";
    Swal.fire({ icon: "error", title: "Error", text: searchError.value });
  } finally {
    searchLoading.value = false;
  }
};

// Registration Form
const formData = reactive({
  projectUrl: "",
  category: null,
});

const uploads = reactive([
  { name: "idCard", label: "Subir cédula de identidad", file: null },
  { name: "rude", label: "Subir documento RUDE", file: null },
  { name: "grade", label: "Subir Nota Solicitud de Participación", file: null },
]);

const categories = [
  { name: "Ciencias Exactas y Naturales", value: "1" },
  { name: "Tecnología e Innovación", value: "2" },
  { name: "Humanística - Sociocultural", value: "3" },
  { name: "Artes Platicas y Visuales", value: "4" },
  { name: "Artes Escénicas y Musicales", value: "5" },
];

const rules = {
  formData: {
    projectUrl: {
      required,
      url,
      googleDriveUrl: {
        $validator: googleDriveUrl,
        $message: "Debe ser un enlace válido de Google Drive.",
      },
    },
    category: { required },
  },
  uploads: {
    $each: {
      file: { required },
    },
  },
};

const v$ = useVuelidate(rules, { formData, uploads });

const formLoading = ref(false);
const submitAttempted = ref(false);

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const ALLOWED_TYPE = "application/pdf";

const onFileSelect = (event, index) => {
  const file = event.files[0];
  if (!file) return;
  if (file.type !== ALLOWED_TYPE) {
    Swal.fire({
      icon: "error",
      title: "Archivo inválido",
      text: "Solo se permiten archivos PDF.",
    });
    uploads[index].file = null;
    return;
  }
  if (file.size > MAX_FILE_SIZE) {
    Swal.fire({
      icon: "error",
      title: "Archivo demasiado grande",
      text: "El archivo no debe superar los 5MB.",
    });
    uploads[index].file = null;
    return;
  }
  uploads[index].file = file;
  submitAttempted.value = false;
};

const handleSubmit = async () => {
  v$.value.$touch();
  submitAttempted.value = true;
  if (v$.value.$invalid) return;

  // Check for missing files
  const missingFiles = uploads
    .filter((item) => !item.file)
    .map((item) => item.label);
  if (missingFiles.length > 0) {
    return;
  }

  // Show confirmation dialog
  const confirmation = await Swal.fire({
    title: "¿Confirmar registro?",
    text: "¿Está seguro que desea registrar al estudiante?",
    icon: "question",
    showCancelButton: true,
    confirmButtonText: "Sí, registrar",
    cancelButtonText: "Cancelar",
    reverseButtons: true,
    customClass: {
      confirmButton: "p-button p-component",
      cancelButton: "p-button p-component p-button-secondary",
    },
    buttonsStyling: false,
  });

  // If user cancels, don't proceed
  if (!confirmation.isConfirmed) {
    return;
  }

  formLoading.value = true;

  try {
    const form = new FormData();

    // Append files
    uploads.forEach((item) => {
      if (item.file) form.append(item.name, item.file);
    });

    // Append form data
    form.append("studentId", student.value.codigo_rude);
    form.append("projectUrl", formData.projectUrl);
    form.append("category", formData.category);

    // Submit to API
    const response = await axios.post(
      "/institution/distrito-phase-register",
      form,
      {
        headers: { "Content-Type": "multipart/form-data" },
      }
    );

    Swal.fire({
      icon: "success",
      title: "Éxito",
      text: "Registro completado exitosamente",
    });
    visible.value = false;
    emit("selectSchool");
    resetForm();
  } catch (error) {
    console.error(error);
    Swal.fire({
      icon: "error",
      title: "Error",
      text: error.response?.data?.message || "Error al registrar",
    });
  } finally {
    formLoading.value = false;
  }
};

const resetForm = () => {
  formData.projectUrl = "";
  formData.category = null;
  uploads.forEach((item) => (item.file = null));
  v$.value.$reset();
  submitAttempted.value = false;
};

// Watch dialog visibility to reset form on close
watch(visible, (val, oldVal) => {
  if (oldVal && !val) {
    resetForm();
  }
});

function downloadReport() {
  emit("getReport");
}
</script>

<style scoped>
.search-container {
  padding: 1.5rem;
}

.search-box {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.search-input-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.search-input-group :deep(.p-inputtext),
.search-input-group :deep(.p-button) {
  width: 100%;
  min-width: 0;
  flex: unset;
}

/* Registration Form Styles */
.registration-panel {
  margin-top: 1.5rem;
}

.form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
}

.two-panel-grid {
  align-items: flex-start;
}

.info-panel {
  background: #f8fafc;
  border-radius: 1rem;
  padding: 2rem 1.5rem;
  margin-bottom: 2rem;
  box-shadow: 0 2px 8px rgba(60, 60, 60, 0.06);
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.info-header {
  font-size: 1.2rem;
  font-weight: 600;
  color: #6366f1;
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

@media (max-width: 768px) {
  .form-grid {
    grid-template-columns: 1fr;
  }
  .two-panel-grid {
    grid-template-columns: 1fr;
  }
  .info-panel,
  .upload-section {
    margin-bottom: 1.5rem;
  }
}

.upload-section {
  display: flex;
  flex-direction: column;
  gap: 2rem;
  background: #f8fafc;
  border-radius: 1rem;
  padding: 2rem 1.5rem;
  margin-bottom: 2rem;
  box-shadow: 0 2px 8px rgba(60, 60, 60, 0.06);
}

.upload-header {
  font-size: 1.2rem;
  font-weight: 600;
  color: #3b82f6;
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.improved-upload-item {
  background: #fff;
  border-radius: 0.75rem;
  padding: 1.25rem 1rem;
  box-shadow: 0 1px 4px rgba(60, 60, 60, 0.04);
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  border: 1px solid #e0e7ef;
  transition: box-shadow 0.2s;
}
.improved-upload-item:hover {
  box-shadow: 0 4px 16px rgba(60, 60, 60, 0.1);
  border-color: #a5b4fc;
}

.upload-label-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.5rem;
}
.upload-label {
  font-weight: 500;
  color: #334155;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}
.selected-file {
  font-size: 0.95rem;
  color: #059669;
  background: #e0f2f1;
  border-radius: 0.5rem;
  padding: 0.2rem 0.7rem;
  display: flex;
  align-items: center;
  gap: 0.3rem;
}

.upload-item :deep(.p-fileupload) {
  width: 100%;
}

.field {
  margin-bottom: 1.5rem;
}

.full-width {
  width: 100% !important;
  min-width: 0;
  box-sizing: border-box;
}

.upload-item :deep(.p-fileupload) {
  width: 100% !important;
}

label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: var(--text-color-secondary);
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-top: 2rem;
  padding-top: 1rem;
  border-top: 1px solid var(--surface-border);
}

.p-error {
  color: var(--red-500);
  font-size: 0.875rem;
  margin-top: 0.25rem;
  display: block;
}

@media (max-width: 768px) {
  .form-grid {
    grid-template-columns: 1fr;
  }
}
</style>

<style>
/* Ensure SweetAlert2 popup is always above dialogs */
.swal2-container {
  z-index: 3000 !important;
}
</style>
